import React from "react";
import { Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "../../shadcn/ui/Button";
import { Badge } from "../../shadcn/ui/Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../shadcn/ui/DropdownMenu";

export type HeaderNotificationItem = {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
};

export type HeaderNotifications = {
  unreadCount: number;
  items: HeaderNotificationItem[];
  loading?: boolean;
  refresh?: () => void;
  onOpenNotification: (id: string) => void;
  onMarkAllRead: () => void;
};

type HeaderNotificationsSlotProps = {
  notifications: HeaderNotifications | undefined;
  showNotificationsButton: boolean;
  isAuthenticated: boolean;
  variant: "compact" | "comfortable" | "figma";
};

function formatNotificationTime(createdAt: string): string {
  try {
    return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
  } catch {
    return "";
  }
}

function NotificationListContent({
  notifications,
}: {
  notifications: HeaderNotifications;
}) {
  const { items, loading, unreadCount, onOpenNotification, onMarkAllRead } = notifications;

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
        <DropdownMenuLabel className="p-0 text-black font-semibold">
          Notifications
        </DropdownMenuLabel>
        {unreadCount > 0 ? (
          <button
            type="button"
            className="text-xs font-medium text-[#F97316] hover:text-orange-700 shrink-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMarkAllRead();
            }}
          >
            Mark all read
          </button>
        ) : null}
      </div>
      <div className="max-h-80 overflow-y-auto py-1">
        {loading ? (
          <div className="px-3 py-6 text-sm text-gray-500 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-3 py-6 text-sm text-gray-500 text-center">No notifications yet.</div>
        ) : (
          items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              className={[
                "flex items-start gap-3 px-3 py-2.5 cursor-pointer rounded-none",
                item.isRead ? "focus:bg-gray-50" : "bg-orange-50/60 focus:bg-orange-50",
              ].join(" ")}
              onSelect={(e) => {
                e.preventDefault();
                onOpenNotification(item.id);
              }}
            >
              <span
                className={[
                  "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                  item.isRead ? "bg-transparent" : "bg-[#F97316]",
                ].join(" ")}
                aria-hidden
              />
              <div className="min-w-0 flex-1 text-left">
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={[
                      "text-sm truncate",
                      item.isRead ? "font-normal text-gray-700" : "font-semibold text-gray-900",
                    ].join(" ")}
                  >
                    {item.title}
                  </span>
                  <span className="shrink-0 text-[10px] text-gray-400 whitespace-nowrap">
                    {formatNotificationTime(item.createdAt)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{item.message}</p>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </div>
      {items.length > 0 ? (
        <>
          <DropdownMenuSeparator className="my-0" />
          <div className="px-3 py-2 text-[11px] text-gray-400 text-center">
            {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
          </div>
        </>
      ) : null}
    </>
  );
}

export function HeaderNotificationsSlot({
  notifications,
  showNotificationsButton,
  isAuthenticated,
  variant,
}: HeaderNotificationsSlotProps) {
  if (!showNotificationsButton || !isAuthenticated) return null;

  const unreadCount = notifications?.unreadCount ?? 0;
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const ariaLabel =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications";

  const iconClass = variant === "compact" ? "h-4 w-4" : "h-5 w-5";
  const badgeClass =
    variant === "comfortable" || variant === "figma"
      ? "absolute -right-1 -top-1 flex min-w-[16px] items-center justify-center rounded-full bg-[#dc2626] px-0.5 text-[9px] font-semibold leading-3 text-white"
      : "absolute -top-2 -right-2 h-5 min-w-[1.25rem] px-1 text-xs bg-red-600 text-white";

  if (!notifications) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          variant === "comfortable"
            ? "relative h-9 w-9 p-0 text-gray-600 hover:text-[#F97316] hover:bg-orange-50 outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            : "relative hover:bg-orange-100 text-black outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
        }
        aria-label={ariaLabel}
      >
        <Bell className={iconClass} />
      </Button>
    );
  }

  const { refresh } = notifications;

  if (variant === "figma") {
    return (
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) refresh?.();
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2.5 rounded-lg outline-none ring-offset-2 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#F97316]"
            aria-label={ariaLabel}
          >
            <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-orange-500">
              <Bell className="size-5 text-white" aria-hidden />
              {unreadCount > 0 ? (
                <span className={badgeClass}>{badgeLabel}</span>
              ) : null}
            </div>
            <div className="hidden min-w-[88px] flex-col text-left sm:flex">
              <span className="text-[11px] font-normal leading-4 text-[#495057]">Notifications</span>
              <span className="text-[13px] font-normal leading-snug text-[#1A2A3D]">
                {unreadCount > 0 ? `Unread (${badgeLabel})` : "All caught up"}
              </span>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 sm:w-96 bg-white p-0 overflow-hidden">
          <NotificationListContent notifications={notifications} />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) refresh?.();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={
            variant === "comfortable"
              ? "relative h-9 w-9 p-0 text-gray-600 hover:text-[#F97316] hover:bg-orange-50 outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              : "relative hover:bg-orange-100 text-black outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          }
          aria-label={ariaLabel}
        >
          <Bell className={iconClass} />
          {unreadCount > 0 ? (
            <Badge className={badgeClass}>{badgeLabel}</Badge>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 sm:w-96 bg-white p-0 overflow-hidden">
        <NotificationListContent notifications={notifications} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
